export default {
  // Toolbar
  toolbar: {
    searchPlaceholder: "Search samples",
    refreshTooltip: "Refresh samples",
    filterTooltip: "Filter samples",

    tabs: {
      samples: "Samples",
      visits: "Visits",
    },
  },

  // Sample list
  samples: {
    title: "Samples",
    noSamples: "There are currently no samples available.",
    noSamplesSubtitle: "Samples will appear here when they are ready for validation.",
    refreshSamples: "Refresh",
    noResults: 'No samples found for "{query}"',
    noSamplesMatchFilters: "No samples match the filters",
    clearFilters: "Edit Filters",
    errorLoading: "An error occurred while loading samples.",
    error404: "API endpoint not found (404). Please check your configuration.",
    error500: "Server error (500). Please try again later.",
    errorAuth: "Authentication error. Please log in again.",
    errorNetwork: "Network error. Please check your internet connection.",
    retry: "Retry",

    actions: {
      highPriority: "High Priority",
      more: "More Actions",
      edit: {
        title: "Edit Sample Barcode",
        description: "Change the barcode for this sample.",
      },
      info: {
        title: "Sample Information",
        description: "View sample details",
      },
      rejectAll: {
        title: "Reject All Results",
        description: "Reject all pending results for this sample.",
      },
    },
  },

  // Patient info
  patient: {
    dateOfBirth: "Date of birth",
    male: "M",
    female: "F",
  },

  // Visit info
  visit: {
    tooltips: {
      visitId: "Visit ID",
      visitDate: "Date of visit",
      visitCategory: "Visit category",
      visitLocation: "Visit location",
      requester: "Requester",
      notes: "Visit notes",
      doctor: "Ordering doctor",
    },
  },

  // Results
  results: {
    noDataAvailable: "No Data Available",
    noDataAvailableSubtitle: "This sample has no results or visit information available.",
    edited: "edited",
    pending: {
      title: "Pending Results",
      rejectAll: "Reject All",
      acceptAll: "Accept All",
      reject: "Reject",
      accept: "Accept",
      addAndAccept: "Add & Accept",
      moreActions: "More actions",
      expand: "Expand All",
      collapse: "Collapse All",
      viewHistory: "View History",
      viewHistoryDescription: "View patient history for this test.",
      flagsDescription: "View flags associated with this result.",
      rejectDescription: "Reject this result",
      editDescription: "Edit this result",
      resultTimeTooltip: "Time when result was generated by instrument",

      acceptSuccess: "Result accepted successfully",
      acceptError: "Failed to accept result",
      acceptAllSuccess: "All results accepted",
      acceptAllError: "Failed to accept all results",
      rejectSuccess: "Result rejected",
      rejectError: "Failed to reject result",
      rejectAllSuccess: "All results rejected",
      rejectAllError: "Failed to reject all results",

      errors: {
        networkError: "Network error. Please check your connection and try again.",
        serverError: "Server error. Please contact support if this persists.",
        loadingFailed: "Failed to load data. Please refresh and try again.",
        pleaseTryAgain: "Please try again.",
        nonDeterministicProfile:
          "The result profile for this pending test is non-deterministic. Please contact an administrator.",
      },
    },
    current: {
      title: "Current Results",
      resultTimeTooltip: "Time when result was validated and published to LIS",
    },
    editedIndicator: "This result has been manually edited",
    editTooltip: {
      previousValue: "Previous value",
      editedBy: "Edited by",
      editedAt: "Edited at",
    },
    refRange: "Ref. Range",
    viewHistory: "View patient history",
    loadingData: "Loading data...",
  },

  flags: {
    descriptions: {
      0: "Unknown",
      1: "Significantly below reference range",
      2: "Below reference range",
      3: "Slightly below normal",
      4: "Within normal limits",
      5: "Slightly above normal",
      6: "Above reference range",
      7: "Critically high level",
    },
  },

  // Alerts
  alerts: {
    visitMissing: {
      title: "Visit Information Missing",
      message: "There is no visit information associated with this sample.",
    },
    noResults: {
      title: "No Results Found",
      message: "There are no results associated with this sample.",
    },
    flaggingMissing: {
      title: "Flagging Information Missing",
      message: "There is no flagging information associated with this sample.",
    },
  },

  // Dialogs
  dialogs: {
    // Edit Sample Dialog
    editSample: {
      title: "Edit Sample Barcode",
      subtitle: "Barcode: {barcode} / {type}",
      saveBarcode: "Edit Barcode",
      description: "You're editing the barcode for sample: <strong>{barcode}</strong>",
      newBarcodeLabel: "New Barcode",
      hint: "Barcode can contain only letters, numbers, dash (-) and underscore (_)",
      errors: {
        required: "Barcode is required",
        barcodeExists: "A sample with this barcode already exists",
        containsQuotes: "Barcode cannot contain quotes (' or \")",
        containsBackslash: "Barcode cannot contain backslash (\\)",
        containsSpecialChars: "Barcode cannot contain special characters (newline, tab)",
        invalidCharacters: "Barcode can only contain letters, numbers, dash (-) and underscore (_)",
        networkError: "Network error - please try again",
      },
      success: "Sample barcode updated successfully",
      error: "Failed to update sample barcode",
    },

    // Edit Result Dialog
    editResult: {
      title: "Edit Result",
      subtitle_test: "Test Code",
      subtitle_id: "Result ID",
      fields: {
        testName: "Test Name",
        originalResult: "Original Result",
        newResult: "New Result",
        reason: "Reason for Edit (Optional)",
        reasonPlaceholder: "e.g., Corrected instrument error, Manual recalculation",
        units: "Units",
        range: "Reference Range",
        resultTime: "Date / Time",
      },
      preview: {
        title: "Parsed Value",
        parsed: "Will be saved as",
      },
      changePreview: "Change",
      validation: {
        required: "This field is required",
        invalidNumeric: "Please enter a valid numeric value",
        belowRange: "Below range: {range}",
        aboveRange: "Above range: {range}",
        withinRange: "Within range: {range}",
      },
      success: "Result updated successfully",
      error: "Failed to update result",
    },

    // Sample Info Dialog
    sampleInfo: {
      subtitle_type: "Sample Type",
      subtitle_visit: "Visit",
      subtitle_results: "Results",
    },

    // Filter Samples Dialog
    filterSamples: {
      title: "Filter Samples",
      subtitle_none: "No filters active — showing all pending samples",
      subtitle_one: "1 filter active",
      subtitle_many: "{n} filters active",
      unsaved: {
        title: "Unapplied Changes",
        message: "You have made filter changes that haven't been applied yet. Discard them and close?",
        stay: "Back to Filters",
        applyAndContinue: "Apply & Continue",
        discard: "Discard Changes",
      },
      priority: {
        label: "Priority: {status}",
        stat: "STAT only",
        all: "all samples",
        hint_off: "All pending samples are shown. Turn ON to restrict the list to STAT (urgent) samples only.",
        hint_on:
          "Only STAT (urgent) samples are shown — results that need immediate attention ahead of routine work. Turn OFF to show all samples.",
      },
      fields: {
        locations: "Locations",
        locations_hint:
          "A location is a lab room or department that groups instruments. Limits the list to samples from instruments in the selected rooms.",
        instruments: "Instruments",
        instruments_hint:
          "Limits the list to samples received from specific analyzers. Useful when reviewing output from a single device or investigating a specific machine.",
        sampleTypes: "Sample Types",
        sampleTypes_hint:
          "The material being analysed — e.g. serum, urine, whole blood. Limits the list to samples of the selected material type.",
        visitTypes: "Visit Types",
        visitTypes_hint:
          "Classifies the clinical encounter — e.g. emergency, outpatient, inpatient. Limits the list to samples from the selected care settings.",
        requesters: "Requesters",
        requesters_hint:
          "The department or clinic that ordered the tests. Limits the list to samples ordered by the selected wards or units.",
        doctors: "Doctors",
        doctors_hint:
          "The physician who ordered the tests. Limits the list to samples ordered by the selected doctors.",
      },
    },
    // Patient History Dialog
    patientHistory: {
      title: "Patient History - {testName}",
      subtitle: "{count} historical result | {count} historical results",
      loading: "Loading patient history...",
      noHistory: "No historical results found for this test",
      range: "Range: {range}",

      // Tabs
      tabs: {
        data: "Data",
        chart: "Chart",
      },

      // Table headers
      table: {
        visitId: "Visit ID",
        resultTime: "Date / Time",
        result: "Result",
      },

      // Chart labels
      chart: {
        results: "Results",
        normalRange: "Ref. Range",
        currentVisit: "Current Visit",
        current: "Current",
        date: "Date",
        value: "Units",
        alertTitle: "Still In Development",
        alertMessage: "The chart feature is under development and will be updated in a future releases.",
      },
    },

    // Reject All Dialog
    rejectAll: {
      title: "Reject All Results",
      subtitle: "Sample: {barcode} / {type}",
      warning: "All pending results associated with this sample will be permanently deleted.",
      confirm: "Reject All Results",
    },

    // Reject Result Dialog
    rejectResult: {
      title: "Reject Result",
      subtitle: "Test: {testName}",
      resultLabel: "Result",
      currentValueLabel: "Current Result",
      unitsLabel: "Units",
      referenceRangeLabel: "Ref. Range",
      sampleTypeLabel: "Sample Type",
      resultTimeLabel: "Date / Time",
      currentValue: "Current value: {value}",
      warning: "This result will be permanently deleted from the pending queue.",
      confirm: "Reject Result",
    },

    flags: {
      title: "Flags",
      subtitle: "Flags raised for this result by the instrument or processor.",
      headers: {
        level: "Level",
        source: "Source",
        code: "Code",
        description: "Description",
      },
      empty: "No flags are attached to this result.",
      sources: {
        Instrument: "Instrument",
        Processor: "Processor",
      },
      levels: {
        0: "Unknown",
        1: "Information",
        2: "Warning",
        3: "Error",
        4: "Critical",
      },
    },
  },

  signalr: {
    connected: "Real-time updates active",
    disconnected: "Real-time updates disconnected",
    reconnecting: "Reconnecting to real-time updates...",
    barcodeChanged: "Barcode changed: {old} → {new}",
    connectionFailed:
      "Failed to connect to SignalR after {attempts} attempts. Page will work, but without real-time updates.",
    resultsAdded: "{count} new results for {barcode}",
    resultsDeleted: "{count} deleted results for {barcode}",
    resultsModified: "{count} modified results for {barcode}",
    sampleAdded: "New sample with barcode: {barcode} added",
    sampleRemoved: "Sample {barcode} has been removed",
  },
};
